/**
 * postLoginTarget.js — PROD-38 (patient half, P3). Where a patient lands after signing in.
 *
 * ★★★ THE MEASUREMENT (P3, 2026-10-06; md11 measured the same in the md panel). Auth on the web
 *   lives in sessionStorage on purpose (utils/authStorage.js: it dies with the tab), so a hard
 *   load in the SAME tab keeps the session, and a NEW tab or a SECOND ORIGIN has none. The
 *   patient app had both:
 *   - a second origin: orella-app.vercel.app serves the same build as app.orellahealth.com
 *     (live 2026-10-06: both 200, the same bundle and VERSION v242.238.3; index.html's og:url /
 *     og:image / twitter:url / twitter:image named the alias, so a shared preview opened it);
 *   - a new tab losing the asked-for route: ProtectedRoute kept only a /case/ deep link for after
 *     the login (v223.3.0 F4); any other protected route (Profile, Visits, Settings, a doctor's
 *     page) ended on /home after signing in.
 *   vercel.json now forwards the alias to app.orellahealth.com (host-scoped, path kept), and
 *   ProtectedRoute keeps the route the tab was LOADED on for after the login. Storage is not
 *   changed: a new tab still asks for a login.
 *
 * ★★ ONLY AN IN-APP PATH IS EVER FOLLOWED: a string that starts with exactly one "/", with no
 *   scheme, no backslash and no control character, and not a public route (a login that returns
 *   to /welcome or /signin would loop; a reset or invite link carries a token that is never
 *   kept). The stored value is checked again when it is read.
 *
 * ★ NO IMPORTS, so scripts/check-canonical-host.mjs executes it in plain node.
 */

export const POST_LOGIN_KEY = 'orella_post_login_redirect'

// Public and pre-login routes (App.jsx) and /home (where every sign-in lands anyway).
const NOT_A_TARGET = [
  '/home', '/landing', '/sign-agreement', '/dev', '/splash', '/welcome', '/signup', '/signin',
  '/email', '/verify', '/security', '/2fa-upgrade', '/forgot-password', '/reset-password',
  '/two-factor-reset',
  '/join', '/onboarding',
]

/** The in-app path to return to, or null when `raw` is not one. */
export function safeReturnPath(raw) {
  if (typeof raw !== 'string') return null
  if (raw.length < 2 || raw.length > 2048) return null
  if (raw[0] !== '/' || raw[1] === '/' || raw[1] === '\\') return null
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(raw)) return null
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(raw)) return null
  const pathOnly = raw.split(/[?#]/)[0]
  if (pathOnly === '/' || pathOnly.endsWith('//')) return null
  if (NOT_A_TARGET.some((p) => pathOnly === p || pathOnly.startsWith(p + '/'))) return null
  return raw
}

/**
 * What ProtectedRoute keeps for after the login, or null. A /case/ deep link is kept (v223.3.0
 * F4) unless a sign-out has happened in this page load (`afterSignOut`); any other protected route
 * only when it is the route this page load started on and nobody has signed in during it
 * (`firstLoad`) — a hard load, a new tab, a link from a message — so an ordinary sign-out from
 * Settings still lands on /home next time.
 *
 * ★ PROD-687 (P3 repair 2): `afterSignOut` — someone was signed in during this load and is not any
 *   more (menu sign-out, idle timeout, a session refused mid-load, account deleted). The route the
 *   tab shows at that moment belongs to the person who just left; ProtectedRoute's render after
 *   the sign-out used to keep a /case/ route, so the next person signing in on that tab landed on
 *   the previous person's case screen. Nothing is kept then; a NEW tab (or a hard load) opened on
 *   a /case/ link has had no sign-in in its load and still keeps it, as F4 intends.
 */
export function postLoginCapture(location, { firstLoad = false, afterSignOut = false } = {}) {
  if (!location || typeof location !== 'object') return null
  if (afterSignOut) return null
  const safe = safeReturnPath(`${location.pathname || ''}${location.search || ''}`)
  if (!safe) return null
  if (safe.startsWith('/case/')) return safe
  return firstLoad ? safe : null
}

/** Where HomeScreen goes after a sign-in: the stored value, checked again, or null. */
export function postLoginResume(stored) {
  return safeReturnPath(stored)
}

/**
 * PROD-38 repair (N3): "has anyone signed in during this page load", for postLoginCapture's
 * `firstLoad`. A session counts only once it is KNOWN to be good: the boot validation of a
 * rehydrated session has resolved and the store is still signed in (or a later sign-in, which
 * the server validated at login). Counting it as soon as a rehydrated session was SEEN meant a
 * hard load with an expired token marked the load as "signed in" a moment before the validation
 * signed it out, and the asked-for route (other than a /case/ link) was lost; a new tab kept it.
 *
 * `seen(validate, isAuthenticated)` is called by ProtectedRoute whenever it renders with a
 * session: the first call runs `validate` (the store's validateSession, once per load); while it
 * is pending nothing is counted; once it has settled, the store decides (isAuthenticated()).
 */
export function createLoadSignIn() {
  let boot = 'unchecked' // 'unchecked' | 'pending' | 'done'
  let signedIn = false
  return {
    seen: (validate, isAuthenticated) => {
      if (boot === 'unchecked') {
        boot = 'pending'
        const settle = () => { boot = 'done'; if (isAuthenticated()) signedIn = true }
        let p
        try { p = validate() } catch { p = null }
        Promise.resolve(p).then(settle, settle)
      } else if (boot === 'done') {
        signedIn = true
      }
    },
    get signedIn() { return signedIn },
  }
}
